import React from 'react';
import {
    SimpleShowLayout,
    ListButton,
    EditButton,
    ShowButton,
    TextField,
    BooleanField,
    SimpleForm,
    LongTextInput,
    TextInput,
    NumberInput,
    DisabledInput,
    maxValue,
    minValue,
    SelectInput,
    required
} from 'admin-on-rest';
import FilterWithSubmit from '../admin/FilterWithSubmit';
import List from '../admin/List';
import ShowWithType from '../admin/ShowWithType';
import Create from '../admin/Create';
import EditWithType from '../admin/EditWithType';
import DiscountDuplication from '../components/DiscountDuplication';
import DiscountItemTables from '../components/DiscountItemTables';
import ValidatedProductTextInput from '../components/ValidatedProductTextInput';
import ListTableContainer from '../components/ListTableContainer';
import ProductTypesSelectInput from '../components/ProductTypesSelectInput';
import {HOURS, MINUTES_QUARTER, AMPM} from '../shared/dateTimeLists';
import {DependentInput} from 'aor-dependent-input';
import ViewButtonWithType from '../buttons/ViewButtonWithType';
import DuplicateTextButton from '../buttons/DuplicateTextButton';
import DuplicateButton from '../buttons/DuplicateButton';
import EditButtonWithType from '../buttons/EditButtonWithType';
import {RefreshButton} from 'admin-on-rest/lib/mui/button';
import {CardActions} from 'material-ui/Card';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import FuseTheme from '../global/theme';
import {viewStyles} from '../shared/styles-view';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/editor/money-off';
import FormattedDateTextInput from '../components/FormattedDateTextInput';

export const fuseDiscountIcon = Icon;

const styles = {
    discountDiagram: {
        width: 300
    },
    block: {display: 'block'},
    inline: {display: 'inline-block'},
    fieldSmall: {flex: '0 1 10%', display: 'inline-block', maxWidth: '100px', overflow: 'hidden'},
    fieldMedium: {flex: '0 1 10%', display: 'inline-block', maxWidth: '160px', overflow: 'hidden'},
    fieldWrap: {display: 'inline-block', maxWidth: '50vw', overflowWrap: 'break-word'},
    inlineDate: {
        flex: '1 1 50%',
        display: 'inline-block',
        width: '100%',
        fontSize: '14px',
        color: FuseTheme.palette.lightTextColor,
        margin: '2em 0 0 0'
    },
    separator: {
        display: 'block',
        width: '100%',
        borderBottom: '1px solid ' + FuseTheme.palette.lightTextColor,
        margin: '1em 0 1em 0'
    },
    breakRight: {clear: 'right'}
};

const getBasePathUpOne = (basePath) => {
    return basePath
        .split('/')
        .slice(0, -1)
        .join('/');
};

const number = value => value && isNaN(Number(value)) ? 'Must be a number' : undefined;

/*
 * View: List
 */

// Create Filter
const DiscountFilter = (props) => (
    <FilterWithSubmit {...props} updateOnChange={true} displayButton={false}>
        <ProductTypesSelectInput
            {...props}
            additionalChoices={[{
                id: 'global',
                name: 'global',
                display_name: 'Global (All Product Types)',
                first: true
            }]}
            source="type"
            optionValue="name"
            label="Select Product Type"
            validate={[required]}
            allowEmpty
            alwaysOn
            style={{...viewStyles.filterInline, ...viewStyles.filterThird}}
        />
    </FilterWithSubmit>
);

// Set title for List Discounts
const ListTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar} className="avatar-title"/>Discounts</span>;

const ListDescription = () => {
    return (
        <span>
            <img src="img-diagram-Discount-List.png" alt="Diagram of Products - Discounts"
                 style={{...viewStyles.diagramRight, ...styles.discountDiagram}}/>
            <p>Product <strong>Discounts</strong> can be used to create discounts and coupons for specific Product
                Units, multiple Product Units, or Categories.
                </p>
            <p>Sort Discounts by columns. Click a Discount's View icon to view its details, or Edit to modify the Discount.
                Click <strong>Create</strong> to add a new Discount.</p>
            <hr style={viewStyles.break}/>
        </span>
    );
};

const ListDescriptionViewOnly = () => {
    return (
        <span>
            <img src="img-diagram-Discount-List.png" alt="Diagram of Products - Discounts"
                 style={{...viewStyles.diagramRight, ...styles.discountDiagram}}/>
            <p>Product <strong>Discounts</strong> can be used to create discounts and coupons for specific Product
                Units, multiple Product Units, or Categories.
                </p>
            <p>Sort Discounts by columns. Click a Discount's View icon to view its details.</p>
            <hr style={viewStyles.break}/>
        </span>
    );
};

const ListColumns = [
    {source: 'id', label: 'ID', type: 'text'},
    {source: 'name', label: 'Name', type: 'text'},
    {source: 'item_action_limit', label: 'Action Limit', type: 'text'},
    {source: 'valid_from_tz', label: 'Valid From', type: 'text', width: 180},
    {source: 'valid_to_tz', label: 'Valid To', type: 'text', width: 180},
    {source: '', label: '', component: <ViewButtonWithType/>, filterable: false, sortable: false, width: 90},
    {source: '', label: '', component: <EditButtonWithType/>, filterable: false, sortable: false, width: 90},
    {source: '', label: '', component: <DuplicateButton/>, filterable: false, sortable: false, width: 90},
    {source: 'type', label: '', style: viewStyles.hidden},
    {source: 'id', label: '', style: viewStyles.hidden}
];

const ListColumnsViewOnly = [
    {source: 'id', label: 'ID', type: 'text'},
    {source: 'name', label: 'Name', type: 'text'},
    {source: 'item_action_limit', label: 'Action Limit', type: 'text'},
    {source: 'valid_from_tz', label: 'Valid From', type: 'text', width: 180, style: viewStyles.noWrap},
    {source: 'valid_to_tz', label: 'Valid To', type: 'text', width: 180},
    {source: '', label: '', component: <ViewButtonWithType/>, filterable: false, sortable: false, width: 90},
    {source: 'type', label: '', style: viewStyles.hidden},
    {source: 'id', label: '', style: viewStyles.hidden}
];
export const fuseDiscountList = (props) => (
    <List
        title={ListTitle}
        desc={<ListDescription/>}
        listType="table"
        filters={<DiscountFilter/>}
        requiresFilter={true}
        allowQueryString={false}
        pagination={null}
        {...props}
    >
        <ListTableContainer
            {...props}
            columns={ListColumns}
            perPage={20}
            currentSort={{field: 'id', order: 'ASC'}}
        />
    </List>
);
export const fuseDiscountListViewOnly = (props) => (
    <List
        title={ListTitle}
        desc={<ListDescriptionViewOnly/>}
        listType="table"
        filters={<DiscountFilter/>}
        requiresFilter={true}
        allowQueryString={false}
        pagination={null}
        {...props}
    >
        <ListTableContainer
            {...props}
            columns={ListColumnsViewOnly}
            perPage={20}
            currentSort={{field: 'id', order: 'ASC'}}
        />
    </List>
);


/*
 * View: Show
 */

// Set title for Show Discount
const ShowTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>{record ? `${record.name}` : 'Discount'}</span>;
};

const ShowDescription = () => {
    return (
        <span>
            <p>Review Discount details below. To edit this Discount, click <strong>Edit</strong> above.</p>
        </span>
    );
};

// Replace Actions for Show Discounts
const ShowActions = ({basePath, data, canEdit}) => (
    <CardActions style={viewStyles.actions}>
        <ListButton basePath={getBasePathUpOne(basePath)}/>
        {canEdit &&
            <EditButton basePath={basePath} record={data}/>
        }
        {canEdit &&
            <DuplicateTextButton basePath={getBasePathUpOne(basePath)} record={data}/>
        }
        <RefreshButton/>
    </CardActions>
);

/*
 * Show Discount Table
 */
export const fuseDiscountShow = ({canEdit, ...props}) => (
    <ShowWithType {...props} title={<ShowTitle/>} desc={<ShowDescription/>} actions={<ShowActions canEdit={canEdit}/>}>
        <SimpleShowLayout>
            <TextField source="type" label="Product Type"/>
            <TextField source="id" label="ID / Barcode"/>
            <TextField source="short_code" label="Short Code"/>
            <TextField source="name" label="Description of Discount"/>
            <BooleanField source="automatic" label="Automatic" style={viewStyles.rowShowField}
                          elStyle={styles.fieldSmall}/>
            <span style={viewStyles.fieldSubheader}>
                Valid dates this Discount is active. Date and time applies to any and all time zones.
            </span>
            <TextField source="valid_from_tz" label="Valid From"/>
            <TextField source="valid_to_tz" label="Valid To"/>
            <TextField source="weight" label="Weight"/>
            <TextField source="scope" label="Scope" elStyle={styles.fieldWrap}/>
            <TextField source="item_action" label="Item Action"/>
            <TextField source="item_action_limit" label="Item Action Limit"/>
            <BooleanField source="active" label="Active" style={viewStyles.rowShowField}
                          elStyle={styles.fieldSmall}/>
        </SimpleShowLayout>
    </ShowWithType>
);

/*
 * View: Create form
 */

// Set title for Create Discount
const CreateTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                                  className="avatar-title"/>Create a Discount</span>;

const CreateDescription = () => {
    return (
        <span>
            <p>Product <strong>Discounts</strong> can be used to create discounts and coupons for specific Product
                Units, multiple Product Units, or Categories.</p>
            <p>You can create a standard Discount and manually populate each field, or create a "Buy X Get Y" (BXGY)
                Discount.</p>
            <p>Enter the required fields below.</p>
        </span>
    );
};

// Set defaults for Create form
const defaultCreateValues = {
    active: false,
    automatic: false,
    buy_qty: 1,
    get_qty: 1,
    item_condition: '',
    discount_amount: 1,
    discount_amount_free: 100,
    valid_from_hour: '00',
    valid_from_min: '00',
    valid_from_ampm: 'am',
    valid_to_hour: '11',
    valid_to_min: '59',
    valid_to_ampm: 'pm',
    weight: '0'
};

// Handle Dependent Input checking
const createShowIfBXGY = (value) => value === 'bxgy';
const createShowIfRegular = (value) => value === 'regular';
const createShowBXGYFields = (record) => {
    return !!(record.discount_type && record.discount_type === 'bxgy' && record.type && record.id);
};
const createShowRegularFields = (record) => {
    return !!(record.discount_type && record.discount_type === 'regular' && record.type && record.id);
};
const createShowRemainingFields = (record) => {
    return !!(record.discount_type && record.type && record.id);
};

/*
 * Create Discount form
 */
export const fuseDiscountCreate = (props) => (
    <Create {...props} title={CreateTitle} desc={<CreateDescription/>}>
        <SimpleForm defaultValue={defaultCreateValues} redirect="list">

            <DiscountDuplication/>
            {/* 1. Select Discount Type */}

            <span style={viewStyles.fieldTitle}>
                1. Select Type of Discount:
                <TooltipIcon dataFor="ttDiscountType"/>
                <ReactTooltip id="ttDiscountType" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    Select a Type of Discount to proceed.
                    <ul>
                        <li>
                            A <strong>Regular</strong> Discount displays fields to manually enter values and
                            calculations. You must add a valid Scope, Item Condition, and Item Action.
                            <br/><br/>
                        </li>
                        <li>
                            A <strong>BXGY</strong> ("Buy X Get Y") Discount is specifically for discounts where a
                            Customer must purchase a required set of Products or a required monetary total in order
                            to receive a discount on similar or another set of items.
                        </li>
                        <li>
                            <em>Note: </em> If you choose a Discount Type, fill out fields, then change the Discount
                            Type, all fields associated with that type will reset.
                        </li>
                    </ul>
                </ReactTooltip>
            </span>
            <span style={viewStyles.fieldDescription}>
                A <strong>Regular</strong> Discount requires a valid Scope, Item Condition, and Item Action.
                <br/><br/>
                A <strong>BXGY</strong> Discount provides a form for assembling a "Buy X Get Y" formula, where a
                Customer must purchase a required set of Products or monetary amount in order to receive a discount
                on another set of items.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
            <SelectInput source="discount_type" label="Select Type of Discount"
                         choices={[
                             {id: 'regular', name: 'Regular (Manual)'},
                             {id: 'bxgy', name: 'BXGY (Buy X Get Y)'}
                         ]}/>
            <span style={viewStyles.fieldDescription}>
                Select a type of Discount to proceed. Note: Changing this will reset all data following.
            </span>

            {/* 2. Select Product Type (Regular Discounts) */}

            <DependentInput dependsOn="discount_type" resolve={createShowIfRegular}>

                <hr style={viewStyles.divider}/>

                <span style={viewStyles.fieldTitle}>
                        2. Select Product Type
                </span>
                <ProductTypesSelectInput
                    {...props}
                    additionalChoices={[{
                        id: 'global',
                        name: 'global',
                        display_name: 'Global (All Product Types)',
                        first: true
                    }]}
                    source="type"
                    optionValue="name"
                    label="Select Product Type"
                    required={true}
                    allowEmpty
                />
                <span style={viewStyles.fieldDescription}>
                    Select a Product Type this Discount applies to. If all Product Types, select "Global".
                </span>
            </DependentInput>

            {/* 2. Select Product Type (BXGY Discounts) */}

            <DependentInput dependsOn="discount_type" resolve={createShowIfBXGY}>

                <hr style={viewStyles.divider}/>

                <span style={viewStyles.fieldTitle}>
                        2. Select Product Type
                </span>
                <ProductTypesSelectInput
                    {...props}
                    source="type"
                    optionValue="name"
                    label="Select Product Type"
                    isRequired={true}
                    allowEmpty
                />
                <span style={viewStyles.fieldDescription}>
                    Select a Product Type this Discount applies to.
                </span>
            </DependentInput>

            {/* 3. Check ID Availability / Add Names */}

            <DependentInput dependsOn="type">

                <hr style={viewStyles.divider}/>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

                <span style={viewStyles.fieldTitle}>
                        3. Add Barcode and Name
                </span>
                <span style={viewStyles.fieldDescription}>
                    Enter and verify the unique <strong>Barcode</strong> for this Discount.
                </span>
                <ValidatedProductTextInput
                    formName="record-form"
                    source="id"
                    parentField="type"
                    fieldType="text"
                    label="ID / Barcode*"
                    buttonLabel="Check Availability"
                    successMessage="This ID / Barcode is available."
                    errorDuplicateMessage="A Discount with this Barcode already exists."
                    errorRequiredMessage="A unique ID / Barcode is required."
                    fieldResource="Discounts"
                    isRequired={true}
                />

                <TextInput source="short_code" label="Short Code"/>
                <span style={viewStyles.fieldDescription}>Enter a <strong>Short number sequence</strong> for typing on
                    POS to add Discount.
                    <TooltipIcon dataFor="ttShort"/>
                    <ReactTooltip id="ttShort" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        If needed, enter a <strong>Short Code</strong> for this Discount. Typically this is a
                        shortened version of the barcode ID.
                    </ReactTooltip>
                </span>

                <TextInput source="name" label="Name of Discount" validate={[required]}/>

                <span style={viewStyles.fieldDescription}>Enter a descriptive <strong>Name</strong> for this Discount,
                    detailing its intended requirement and benefit.
                    <TooltipIcon dataFor="ttName"/>
                    <ReactTooltip id="ttName" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        Describe what purchase or action is required by a Customer to qualify for this Discount, and
                        what percentage or reduction is offered by using this Discount. Examples:
                        <br/><br/>
                        <ul>
                            <li>"10% off ProductName"</li>
                            <li>"Buy $100 Get $30 Item Free"</li>
                            <li>"10% off Entire Purchase"</li>
                        </ul>
                    </ReactTooltip>
                </span>

            </DependentInput>


            {/* Fields (Regular): Scope, Condition, Item Action */}

            <DependentInput resolve={createShowRegularFields}>

                <hr style={viewStyles.divider}/>

                <span style={viewStyles.fieldTitle}>
                        4. Add Scope, Item Condition and Item Action
                </span>

                <LongTextInput source="scope" label="Scope" validate={[required]}
                           style={viewStyles.fieldWide} elStyle={viewStyles.fieldWide}
                />

                <TextInput source="item_action" label="Item Action" validate={[required]}/>

            </DependentInput>


            {/* Fields (BXGY): Price Difference, Buy/Get Assembly */}

            <DependentInput resolve={createShowBXGYFields}>

                <hr style={viewStyles.divider}/>

                <span style={viewStyles.fieldTitle}>
                        4a. Set Price Difference and Qty
                </span>
                <span style={viewStyles.fieldDescription}>
                    Determine whether this Discount provides items for free or at a discounted price, then set how
                    many items a customer must purchase to get the free or discounted items.
                </span>
                <hr style={viewStyles.break}/>

                <SelectInput source="price_difference" label="Price Difference"
                             validate={[required]}
                             choices={[
                                 {id: 'percentage', name: 'Percentage Off'},
                                 {id: 'free', name: 'Free'}
                             ]}
                             style={viewStyles.rowField}
                />
                <DependentInput dependsOn="price_difference" value="percentage" style={viewStyles.rowField}>
                    <NumberInput source="discount_amount" label="Discount Percentage" step={0.1}
                                 validate={[number, required, minValue(1, 'Must be at least 1'), maxValue(100, 'Maximum 100')]}
                                 style={viewStyles.rowField}
                    />
                </DependentInput>
                <DependentInput dependsOn="price_difference" value="free" style={viewStyles.rowField}>
                    <DisabledInput source="discount_amount_free" label="Discount Percentage"
                                   style={viewStyles.rowField} elStyle={viewStyles.rowField}/>
                </DependentInput>

                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
                <span style={viewStyles.fieldDescription}>
                    Does this Discount offer items for free, or at a discounted price?
                </span>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
                <span style={viewStyles.fieldDescriptionTop}>
                    Set a <strong>Buy Qty</strong> (how many of an item to purchase) and
                    a <strong>Get Qty</strong> (how many items offered).
                    <TooltipIcon dataFor="ttBuyGetQty"/>
                    <ReactTooltip id="ttBuyGetQty" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        This translates to the "Buy X Get Y" offer. For example, A <strong>Buy Qty</strong> of 2 and
                        a <strong>Get Qty</strong> of 1 means "Buy 2, Get 1" at the chosen Discount Percentage.
                    </ReactTooltip>
                </span>
                <NumberInput source="buy_qty" label="Buy Qty" step={1}
                             validate={[number, required, minValue(1, 'Must be at least 1')]}
                             options={{
                                 min: 1
                             }}
                             style={viewStyles.rowShowField}/>
                <NumberInput source="get_qty" label="Get Qty" step={1}
                             validate={[number, required, minValue(1, 'Must be at least 1')]}
                             options={{
                                 min: 1
                             }}
                             style={viewStyles.rowShowField}/>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

                {/* BXGY Discount Assembly Tables */}

                <DiscountItemTables/>

                <DisabledInput source="scope" label="Scope"
                               validate={[required]}
                               style={viewStyles.hidden} elStyle={viewStyles.hidden}/>
                <DisabledInput source="item_action" label="Item Action"
                               validate={[required]}
                               style={viewStyles.hidden} elStyle={viewStyles.hidden}/>

            </DependentInput>

            <DependentInput resolve={createShowRemainingFields}>

                {/* 5. From / To Dates */}

                <hr style={viewStyles.divider}/>

                <span style={viewStyles.fieldTitle}>
                        5. Select From and To dates and times
                </span>

                <span style={viewStyles.fieldHeader}>
                    Valid From and To dates and times:
                    <TooltipIcon dataFor="ttFrom"/>
                    <ReactTooltip id="ttFrom" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        Set Dates and Times this Discount will be active and deactivated.
                        <br/><br/>
                        Date and time applies to any and all time zones.
                    </ReactTooltip>
                </span>
                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
                <span style={{...viewStyles.fieldDescription, ...viewStyles.break}}>
                    Select a valid From date and time this Discount will become active. Date and time applies to any and all time zones.
                </span>

                <FormattedDateTextInput
                    formName="record-form"
                    formType="create"
                    source="valid_from_string"
                    label="Date"
                    placeholder="MM/DD/YYYY"
                    isRequired={true}
                    style={{
                        ...viewStyles.rowField,
                        ...viewStyles.calendarTopMargin,
                        ...styles.fieldMedium
                    }}
                />
                <SelectInput source="valid_from_hour" label="Hour" optionValue="id" optionText="name"
                             style={{...viewStyles.rowField, ...styles.fieldSmall}}
                             validate={[required]}
                             choices={HOURS}/>
                <SelectInput source="valid_from_min" label="Minutes" optionValue="id" optionText="name"
                             style={{...viewStyles.rowField, ...styles.fieldSmall}}
                             validate={[required]}
                             choices={MINUTES_QUARTER}/>
                <SelectInput source="valid_from_ampm" label="AM/PM" optionValue="id" optionText="name"
                             style={{...viewStyles.rowField, ...styles.fieldSmall}}
                             validate={[required]}
                             choices={AMPM}/>

                <span style={{...viewStyles.fieldDescription, ...viewStyles.break}}>
                    Select a valid To date and time this Discount will become deactivated. Date and time applies to any and all time zones.
                </span>

                <FormattedDateTextInput
                    formName="record-form"
                    formType="create"
                    source="valid_to_string"
                    label="Date"
                    placeholder="MM/DD/YYYY"
                    isRequired={true}
                    style={{
                        ...viewStyles.rowField,
                        ...viewStyles.calendarTopMargin,
                        ...styles.fieldMedium
                    }}
                />
                <SelectInput source="valid_to_hour" label="Hour" optionValue="id" optionText="name"
                             style={{...viewStyles.rowField, ...styles.fieldSmall}}
                             validate={[required]}
                             choices={HOURS}/>
                <SelectInput source="valid_to_min" label="Minutes" optionValue="id" optionText="name"
                             style={{...viewStyles.rowField, ...styles.fieldSmall}}
                             validate={[required]}
                             choices={MINUTES_QUARTER}/>
                <SelectInput source="valid_to_ampm" label="AM/PM" optionValue="id" optionText="name"
                             style={{...viewStyles.rowField, ...styles.fieldSmall}}
                             validate={[required]}
                             choices={AMPM}/>

                {/* 6. Discount Settings */}

                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
                <hr style={viewStyles.divider}/>

                <span style={viewStyles.fieldTitle}>
                        6. Complete Discount Settings
                </span>

                <span style={viewStyles.fieldHeader}>Discount Settings</span>

                <SelectInput source="automatic" label="Automatic"
                             validate={[required]}
                             choices={[
                                 {id: true, name: 'Yes'},
                                 {id: false, name: 'No'}
                             ]}/>
                <span style={viewStyles.fieldDescription}>
                    Is this Discount automatically applied?
                </span>

                <NumberInput source="weight" label="Weight" step={1}
                             validate={[required, number, minValue(0, 'Must be at least 0')]}/>

                <NumberInput source="item_action_limit" label="Item Action Limit" step={1}
                             validate={[number, required, minValue(1, 'Must be at least 1')]}
                             options={{
                                 min: 1
                             }}
                />
                <span style={viewStyles.fieldDescription}>
                    Set a maximum total of times this Discount can be applied.
                </span>

                <SelectInput source="active" label="Active"
                             validate={[required]}
                             choices={[
                                 {id: true, name: 'Active'},
                                 {id: false, name: 'Inactive'}
                             ]}/>

            </DependentInput>

            <TextField source="type" style={viewStyles.hidden} elStyle={viewStyles.hidden}/>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the Discount List.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </Create>
);

/*
 * View: Edit form
 */

// Set title for Edit Discount
const EditTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>Edit {record ? `${record.name}` : 'Discount'}</span>;
};

const EditDescription = () => {
    return (
        <span>
            <p>Modify Discount fields below. Click <strong>Save</strong> to store this Discount.</p>
        </span>
    );
};

// Replace Actions for Edit Discount
const EditActions = ({basePath, data}) => (
    <CardActions style={viewStyles.actions}>
        <ListButton basePath={getBasePathUpOne(basePath)}/>
        <ShowButton basePath={basePath} record={data}/>
        <DuplicateTextButton basePath={getBasePathUpOne(basePath)} record={data}/>
        <RefreshButton/>
    </CardActions>
);

/*
 * Edit Discount form
 */
export const fuseDiscountEdit = (props) => (
    <EditWithType {...props} title={<EditTitle/>} desc={<EditDescription/>} actions={<EditActions/>}>
        <SimpleForm redirect="list">
            <TextField source="type" label="Product Type"/>

            <TextField source="id" label="Discount ID / Barcode #"/>

            <TextInput source="short_code" label="Short Code"/>
            <span style={viewStyles.fieldDescription}>A <strong>Short number sequence</strong> for typing on POS to
                add Discount.
                <TooltipIcon dataFor="ttShort"/>
                <ReactTooltip id="ttShort" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    If needed, a <strong>Short Code</strong> is typically a shortened version of the barcode ID.
                </ReactTooltip>
            </span>

            <TextInput source="name" label="Description of Discount" validate={[required]}/>
            <span style={viewStyles.fieldDescription}>A short <strong>Description</strong> of this Discount.
                <TooltipIcon dataFor="ttName"/>
                <ReactTooltip id="ttName" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    Describe what percentage or reduction is offered by using this Discount.
                </ReactTooltip>
            </span>

            <SelectInput source="automatic" label="Automatic"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Yes'},
                             {id: false, name: 'No'}
                         ]}/>
            <span style={viewStyles.fieldDescription}>
                Is this Discount automatically applied?
            </span>

            <span style={viewStyles.fieldHeader}>
                Valid From date and time:
                <TooltipIcon dataFor="ttFrom"/>
                <ReactTooltip id="ttFrom" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    Set a Date and Time this Discount will be active.
                    <br/><br/>
                    Date and time applies to any and all time zones.
                </ReactTooltip>
            </span>

            <FormattedDateTextInput
                formName="record-form"
                source="valid_from_string"
                label="Date"
                placeholder="MM/DD/YYYY"
                isRequired={true}
                style={{
                    ...viewStyles.rowField,
                    ...viewStyles.calendarTopMargin,
                    ...styles.fieldMedium
                }}
            />

            <SelectInput source="valid_from_hour" label="Hour" optionValue="id" optionText="name"
                         style={{...viewStyles.rowField, ...styles.fieldSmall}}
                         validate={[required]}
                         choices={HOURS}/>
            <SelectInput source="valid_from_min" label="Minutes" optionValue="id" optionText="name"
                         style={{...viewStyles.rowField, ...styles.fieldSmall}}
                         validate={[required]}
                         choices={MINUTES_QUARTER}/>
            <SelectInput source="valid_from_ampm" label="AM/PM" optionValue="id" optionText="name"
                         style={{...viewStyles.rowField, ...styles.fieldSmall}}
                         validate={[required]}
                         choices={AMPM}/>

            <span style={viewStyles.fieldHeader}>
                Valid To date and time:
                <TooltipIcon dataFor="ttTo"/>
                <ReactTooltip id="ttTo" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    Set a Date and Time this Discount will be deactivated.
                    <br/><br/>
                    Date and time applies to any and all time zones.
                </ReactTooltip>
            </span>

            <FormattedDateTextInput
                formName="record-form"
                source="valid_to_string"
                label="Date"
                placeholder="MM/DD/YYYY"
                isRequired={true}
                style={{
                    ...viewStyles.rowField,
                    ...viewStyles.calendarTopMargin,
                    ...styles.fieldMedium
                }}
            />

            <SelectInput source="valid_to_hour" label="Hour" optionValue="id" optionText="name"
                         style={{...viewStyles.rowField, ...styles.fieldSmall}}
                         validate={[required]}
                         choices={HOURS}/>
            <SelectInput source="valid_to_min" label="Minutes" optionValue="id" optionText="name"
                         style={{...viewStyles.rowField, ...styles.fieldSmall}}
                         validate={[required]}
                         choices={MINUTES_QUARTER}/>
            <SelectInput source="valid_to_ampm" label="AM/PM" optionValue="id" optionText="name"
                         style={{...viewStyles.rowField, ...styles.fieldSmall}}
                         validate={[required]}
                         choices={AMPM}/>

            <NumberInput source="weight" label="Weight" step={1}
                         validate={[required, number, minValue(0, 'Must be at least 0')]}/>

            <LongTextInput source="scope" label="Scope"
                       validate={[required]}
                       style={viewStyles.fieldWide} elStyle={viewStyles.fieldWide}/>

            <TextInput source="item_action" label="Item Action"
                       validate={[required]}
                       style={viewStyles.fieldWide} elStyle={viewStyles.fieldWide}/>

            <NumberInput source="item_action_limit" label="Item Action Limit" step={1}
                         validate={[number, required, minValue(1, 'Must be at least 1')]}
                         options={{
                             min: 1
                         }}
            />
            <span style={viewStyles.fieldDescription}>
                Maximum total of times this Discount can be applied.
            </span>

            <SelectInput source="active" label="Active"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Active'},
                             {id: false, name: 'Inactive'}
                         ]}/>

            <TextField source="type" style={viewStyles.hidden} elStyle={viewStyles.hidden}/>

            <span style={viewStyles.fieldInstructions}>
                After clicking <strong>Save</strong>, you will be redirected to the Discount List.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </EditWithType>
);



// WEBPACK FOOTER //
// ./src/Fuse/Product/discount.js